import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudDone from '@mui/icons-material/CloudDone';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export default function GatewayJoinSettings() {
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [message, setMessage] = useState(null);
  const [status, setStatus] = useState({ status: 'disconnected' });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState({
    gatewayUrl: 'https://theradiocloud.com',
    tenantId: '',
    tenantName: '',
    baseUrl: window.location.origin,
    provisionToken: '',
  });
  const checkingRef = useRef(false);

  const applyStatus = useCallback((data) => {
    setStatus(data || { status: 'disconnected' });
    setForm((current) => ({
      ...current,
      gatewayUrl: data?.gatewayUrl || current.gatewayUrl,
      tenantId: data?.tenantId || current.tenantId,
      tenantName: data?.tenantName || current.tenantName,
      baseUrl: data?.baseUrl || current.baseUrl || window.location.origin,
    }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/gateway/status`, { withCredentials: true });
      applyStatus(response.data);
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setLoading(false);
    }
  }, [applyStatus]);

  const pollApproval = useCallback(async ({ silent } = {}) => {
    if (checkingRef.current) return null;
    checkingRef.current = true;
    try {
      const response = await axios.post(`${API_URL}/api/gateway/check`, {}, { withCredentials: true });
      applyStatus(response.data);
      if (response.data?.status === 'connected') {
        setMessage({ severity: 'success', text: 'RadioCloud approved this station. Gateway trust is now active.' });
      } else if (response.data?.status === 'rejected') {
        setMessage({ severity: 'error', text: 'The RadioCloud administrator rejected this join request.' });
      } else if (!silent) {
        setMessage({ severity: 'info', text: 'Still waiting for approval.' });
      }
      return response.data;
    } catch (error) {
      const statusCode = error.response?.status;
      if (statusCode === 404 || statusCode === 400) {
        try {
          const statusResponse = await axios.get(`${API_URL}/api/gateway/status`, { withCredentials: true });
          applyStatus(statusResponse.data);
        } catch (_) {
          applyStatus({ status: 'disconnected' });
        }
      }
      if (!silent) {
        setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
      } else if (statusCode === 404) {
        setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
      }
      return null;
    } finally {
      checkingRef.current = false;
    }
  }, [applyStatus]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (status.status !== 'pending') return undefined;
    const timer = setInterval(() => {
      pollApproval({ silent: true });
    }, 5000);
    return () => clearInterval(timer);
  }, [status.status, pollApproval]);

  const setField = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'tenantName' && !current.tenantId) {
        next.tenantId = slugify(value);
      }
      return next;
    });
  };

  const join = async () => {
    setWorking('join');
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/api/gateway/join`, {
        gatewayUrl: form.gatewayUrl,
        tenantId: form.tenantId,
        tenantName: form.tenantName,
        baseUrl: form.baseUrl,
        publicOrigin: form.baseUrl,
        provisionToken: form.provisionToken,
      }, { withCredentials: true });
      applyStatus(response.data);
      if (response.data.status === 'connected') {
        setMessage({ severity: 'success', text: 'Connected to RadioCloud.' });
      } else {
        setMessage({
          severity: 'info',
          text: 'Join request submitted. A RadioCloud administrator must Approve it in the gateway admin UI.',
        });
      }
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setWorking('');
    }
  };

  const checkNow = async () => {
    setWorking('check');
    setMessage(null);
    await pollApproval({ silent: false });
    setWorking('');
  };

  const cancel = async () => {
    setWorking('cancel');
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/api/gateway/cancel`, {}, { withCredentials: true });
      applyStatus(response.data);
      setMessage({ severity: 'info', text: 'Pending join request cancelled on this station.' });
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setWorking('');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const connected = status.status === 'connected';
  const pending = status.status === 'pending';

  return (
    <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <CloudDone color="primary" />
          <Typography variant="h6">Gateway</Typography>
          <Chip
            size="small"
            label={connected ? 'Connected' : pending ? 'Waiting for approval' : 'Not connected'}
            color={connected ? 'success' : pending ? 'warning' : 'default'}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Join this station to RadioCloud so users can sign in at the shared gateway domain.
          A RadioCloud administrator must approve the request.
        </Typography>

        {message && (
          <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {connected && (
          <Stack spacing={1}>
            <Typography variant="body2"><strong>Gateway:</strong> {status.gatewayUrl}</Typography>
            <Typography variant="body2"><strong>Tenant ID:</strong> {status.tenantId}</Typography>
            {status.tenantName ? (
              <Typography variant="body2"><strong>Name:</strong> {status.tenantName}</Typography>
            ) : null}
          </Stack>
        )}

        {pending && (
          <Stack spacing={2}>
            <Alert severity="info">
              Request queued as <strong>{status.tenantId || form.tenantId}</strong>.
              Open the RadioCloud admin UI, approve it under Pending enrollments, then this page will finish automatically.
            </Alert>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={checkNow} disabled={Boolean(working)}>
                {working === 'check' ? 'Checking…' : 'Check now'}
              </Button>
              <Button color="warning" onClick={cancel} disabled={Boolean(working)}>
                Cancel request
              </Button>
            </Stack>
          </Stack>
        )}

        {!connected && !pending && (
          <Stack spacing={2}>
            <TextField
              label="Gateway URL"
              value={form.gatewayUrl}
              onChange={setField('gatewayUrl')}
              helperText="Usually https://theradiocloud.com"
              fullWidth
            />
            <TextField
              label="Display name"
              value={form.tenantName}
              onChange={setField('tenantName')}
              helperText="Shown in the RadioCloud system picker"
              fullWidth
            />
            <TextField
              label="Tenant ID"
              value={form.tenantId}
              onChange={setField('tenantId')}
              helperText="Permanent slug: lowercase letters, digits, hyphens"
              fullWidth
            />
            <TextField
              label="Public base URL"
              value={form.baseUrl}
              onChange={setField('baseUrl')}
              helperText="HTTPS origin of this station, e.g. https://station.example.com"
              fullWidth
            />
            <Button size="small" onClick={() => setShowAdvanced((open) => !open)} sx={{ alignSelf: 'flex-start' }}>
              {showAdvanced ? 'Hide advanced options' : 'Advanced: provisioning token'}
            </Button>
            <Collapse in={showAdvanced}>
              <TextField
                label="Provisioning token"
                type="password"
                value={form.provisionToken}
                onChange={setField('provisionToken')}
                helperText="Optional. Instant join when the gateway has GW_PROVISION_TOKEN set; skips admin approval."
                fullWidth
              />
            </Collapse>
            <Box>
              <Button
                variant="contained"
                onClick={join}
                disabled={Boolean(working) || !form.gatewayUrl || !form.tenantId || !form.tenantName || !form.baseUrl}
              >
                {working === 'join' ? 'Submitting…' : 'Request to join'}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
